import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { EqCardKey } from '@equaliq/api-model'
import { persistStore } from 'src/persist-store'

export interface SelectedCard {
  id: string
  cardKey: EqCardKey
  sectionLetter: string
  sectionTitle: string
  cardIndex: number
  contractId?: string
  contractName?: string
  contractType?: string
  cardData: any
  timestamp: number
}

// SessionStorage keys
const STORAGE_KEYS = {
  SELECTED_CARDS: 'card-comparison-selected-cards',
  SELECTED_CONTRACTS: 'card-comparison-selected-contracts',
  CURRENT_CONTRACT_ID: 'card-comparison-current-contract-id'
}

interface CardComparisonContextType {
  selectedCards: SelectedCard[]
  addCard: (card: SelectedCard) => void
  removeCard: (cardId: string) => void
  clearSelection: () => void
  isCardSelected: (cardId: string) => boolean
  toggleCard: (card: SelectedCard) => void
  selectedContracts: string[]
  setSelectedContracts: (contracts: string[]) => void
  currentContractId: string | null
  setCurrentContractId: (contractId: string | null) => void
  isLoading: boolean
}

const CardComparisonContext = createContext<CardComparisonContextType | undefined>(undefined)

export const useCardComparison = () => {
  const context = useContext(CardComparisonContext)
  if (!context) {
    throw new Error('useCardComparison must be used within a CardComparisonProvider')
  }
  return context
}

interface CardComparisonProviderProps {
  children: ReactNode
}

export const CardComparisonProvider: React.FC<CardComparisonProviderProps> = ({ children }) => {
  // Initialize state from sessionStorage
  const [selectedCards, setSelectedCards] = useState<SelectedCard[]>([])
  const [selectedContracts, setSelectedContracts] = useState<string[]>([])
  const [currentContractId, setCurrentContractId] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  // Load initial state from persist-store
  useEffect(() => {
    const loadInitialState = async () => {
      const [cards, contracts, contractId] = await Promise.all([
        persistStore.getSession<SelectedCard[]>(STORAGE_KEYS.SELECTED_CARDS),
        persistStore.getSession<string[]>(STORAGE_KEYS.SELECTED_CONTRACTS),
        persistStore.getSession<string>(STORAGE_KEYS.CURRENT_CONTRACT_ID)
      ])

      setSelectedCards(cards ?? [])
      setSelectedContracts(contracts ?? [])
      setCurrentContractId(contractId ?? null)
      setIsInitialized(true)
    }

    loadInitialState()
  }, [])

  // Sync selectedCards to sessionStorage
  useEffect(() => {
    if (isInitialized) {
      persistStore.putSession(STORAGE_KEYS.SELECTED_CARDS, selectedCards)
    }
  }, [selectedCards, isInitialized])

  // Sync selectedContracts to sessionStorage
  useEffect(() => {
    if (isInitialized) {
      persistStore.putSession(STORAGE_KEYS.SELECTED_CONTRACTS, selectedContracts)
    }
  }, [selectedContracts, isInitialized])

  // Sync currentContractId to sessionStorage
  useEffect(() => {
    if (isInitialized) {
      persistStore.putSession(STORAGE_KEYS.CURRENT_CONTRACT_ID, currentContractId)
    }
  }, [currentContractId, isInitialized])

  const addCard = (card: SelectedCard) => {
    setSelectedCards(prev => {
      const exists = prev.find(c => c.id === card.id)
      if (exists) return prev
      return [...prev, card]
    })
  }

  const removeCard = (cardId: string) => {
    setSelectedCards(prev => prev.filter(card => card.id !== cardId))
  }

  const clearSelection = () => {
    setSelectedCards([])
  }

  const isCardSelected = (cardId: string) => {
    return selectedCards.some(card => card.id === cardId)
  }

  const toggleCard = (card: SelectedCard) => {
    if (isCardSelected(card.id)) {
      removeCard(card.id)
    } else {
      addCard(card)
    }
  }

  return (
    <CardComparisonContext.Provider
      value={{
        selectedCards,
        addCard,
        removeCard,
        clearSelection,
        isCardSelected,
        toggleCard,
        selectedContracts,
        setSelectedContracts,
        currentContractId,
        setCurrentContractId,
        isLoading: !isInitialized
      }}
    >
      {children}
    </CardComparisonContext.Provider>
  )
}
